import React, { useContext, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { ProjectSpatialReferenceContext } from '../../../../state/project-reference-state';
import { DataTable, DataTableColumns, DataTableSortType } from '../../../data-table/data-table';
import { FileSelectionButton } from '../../../file-selection-button/file-selection-button';
import { Icon } from '../../../icon/icon';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { InfoBox } from '../../../info-box/info-box';
import { OverlayLoader } from '../../../overlay-loader/overlay-loader';
import { Stack } from '../../../stack/stack';
import { parseImage } from '../../geodata-images-state';

const supportedImageFileTypes = ['.jpg', '.jpeg'];

type TableItem = {
  statusIcon: React.ReactNode;
  filename: string;
  actions: React.ReactNode;
  // Keep original data for reference
  originalData: { filename: string; file: File; imageIdentifier: string };
};

export const UploadImages = () => {
  const { coordinateSystem, geoid } = useContext(ProjectSpatialReferenceContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const context = useContext(GeodataSetupContext);
  const [error, setError] = React.useState<string | null>(null);
  const [failedImageNames, setFailedImageNames] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [imageFiles, setImageFiles] = React.useState<
    { filename: string; file: File; imageIdentifier: string }[]
  >([]);

  // Load existing image files from context on mount
  useEffect(() => {
    if (isDefined(context)) {
      const existingFiles = context.imageFiles;
      if (existingFiles.length > 0) {
        setImageFiles(existingFiles);
      }
    }
  }, [context]);

  // dragCount holds number of elements we have currently dragged into.
  // As long as dragCounts > 0 we are dragging over the droppable area.
  const dragCount = React.useRef(0);
  const [isDropable, setIsDropable] = React.useState(false);

  // Reset drag state when drag ends (including when cancelled)
  useEffect(() => {
    const handleDragEnd = () => {
      dragCount.current = 0;
      setIsDropable(false);
    };

    // Listen for dragend on both document and window to catch all cases
    document.addEventListener('dragend', handleDragEnd);
    window.addEventListener('dragend', handleDragEnd);

    // Also listen for dragleave on document to catch when leaving the page entirely
    const handleDocumentDragLeave = (e: DragEvent) => {
      // Only reset if we're leaving the document entirely (relatedTarget is null or outside)
      if (!e.relatedTarget || (e.relatedTarget as Node).nodeType === Node.DOCUMENT_NODE) {
        dragCount.current = 0;
        setIsDropable(false);
      }
    };

    document.addEventListener('dragleave', handleDocumentDragLeave);

    return () => {
      document.removeEventListener('dragend', handleDragEnd);
      window.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('dragleave', handleDocumentDragLeave);
    };
  }, []);

  const handleFilesSelected = React.useCallback(
    async (files: File[]) => {
      setIsLoading(true);
      setError(null);
      setFailedImageNames([]);

      try {
        const newFiles: { filename: string; file: File; imageIdentifier: string }[] = [];
        const errorCounts = {
          missingGps: 0,
          missingCaptureDate: 0,
          failed: 0,
          invalidFileType: 0,
          duplicate: 0,
          invalidPosition: 0,
        };
        const failedNames: string[] = [];

        for (const file of files) {
          // Validate file extension
          const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
          if (!supportedImageFileTypes.includes(fileExtension)) {
            errorCounts.invalidFileType++;
            failedNames.push(file.name);
            continue;
          }

          try {
            // Parse image to get identifier and check for errors
            const { imageIdentifier, errorStatus, position } = await parseImage(file);

            // Don't add images with errors
            if (errorStatus === 'FAILED_MISSING_GPS_DATA' || !isDefined(position)) {
              errorCounts.missingGps++;
              failedNames.push(file.name);
              continue;
            }

            const coordinates = CoordinateManager.getInstance().wgs84CoordsToProject(
              { longitude: position.longitude, latitude: position.latitude, height: 0 },
              { proj4: coordinateSystem?.proj4 },
            );

            if (errorStatus === 'FAILED_MISSING_CAPTURE_DATE') {
              errorCounts.missingCaptureDate++;
              failedNames.push(file.name);
              continue;
            }
            if (errorStatus === 'FAILED') {
              errorCounts.failed++;
              failedNames.push(file.name);
              continue;
            }
            if (
              !CoordinateManager.getInstance().isValidCoordinate(coordinates, {
                proj4: coordinateSystem?.proj4,
                geoidBounds: geoid?.header,
              })
            ) {
              errorCounts.invalidPosition++;
              failedNames.push(file.name);
              continue;
            }

            const newFile = {
              filename: file.name,
              file: file,
              imageIdentifier: imageIdentifier,
            };

            // Check if file already exists (by imageIdentifier or filename+size)
            // Check against both existing imageFiles and files already added in this batch
            const exists =
              imageFiles.some((f) => f.imageIdentifier === newFile.imageIdentifier) ||
              newFiles.some((f) => f.imageIdentifier === newFile.imageIdentifier);
            if (exists) {
              errorCounts.duplicate++;
              failedNames.push(file.name);
              continue;
            }

            newFiles.push(newFile);
          } catch (parseError) {
            console.error(`Failed to parse image ${file.name}:`, parseError);
            errorCounts.failed++;
            failedNames.push(file.name);
          }
        }

        // Build error messages
        const errorMessages: string[] = [];
        if (errorCounts.missingGps > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesWithMissingGps', {
              count: errorCounts.missingGps,
              ns: 'cloudProcessing',
            }),
          );
        }
        if (errorCounts.missingCaptureDate > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesWithMissingCaptureDate', {
              count: errorCounts.missingCaptureDate,
              ns: 'cloudProcessing',
            }),
          );
        }
        if (errorCounts.failed > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesFailed', {
              count: errorCounts.failed,
              ns: 'cloudProcessing',
            }),
          );
        }
        if (errorCounts.invalidFileType > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesInvalidFileType', {
              count: errorCounts.invalidFileType,
              ns: 'cloudProcessing',
            }),
          );
        }
        if (errorCounts.duplicate > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesDuplicate', {
              count: errorCounts.duplicate,
              ns: 'cloudProcessing',
            }),
          );
        }
        if (errorCounts.invalidPosition > 0) {
          errorMessages.push(
            t('geodataSetup.unableToAddImagesInvalidPosition', {
              count: errorCounts.invalidPosition,
              ns: 'cloudProcessing',
            }),
          );
        }

        if (errorMessages.length > 0) {
          setError(errorMessages.join('. '));
          setFailedImageNames(failedNames);
        }

        if (newFiles.length > 0) {
          const updatedFiles = [...imageFiles, ...newFiles];
          setImageFiles(updatedFiles);

          if (isDefined(context)) {
            context.setImageFiles(updatedFiles);
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [imageFiles, context, t, coordinateSystem, geoid],
  );

  const { getRootProps } = useDropzone({
    noClick: true, // Prevents file picker from being displayed when clicking on inner elements.
    accept: {
      'image/jpeg': supportedImageFileTypes,
    },
    multiple: true,
    onDrop: React.useCallback(
      async (acceptedFiles: File[], rejectedFiles: { file?: File }[]) => {
        dragCount.current = 0;
        setIsDropable(false);
        const allFiles: File[] = [...acceptedFiles];

        // Try to handle files that were rejected due to MIME type but might still be valid
        for (const rejectedFile of rejectedFiles) {
          if (rejectedFile.file) {
            allFiles.push(rejectedFile.file);
          }
        }

        if (allFiles.length > 0) {
          await handleFilesSelected(allFiles);
        } else if (rejectedFiles.length > 0) {
          const rejectedNames = rejectedFiles
            .map((rf) => rf.file?.name)
            .filter((name): name is string => isDefined(name));
          setError(t('geodataSetup.uploadImages.invalidFileType', { ns: 'cloudProcessing' }));
          setFailedImageNames(rejectedNames);
        }
      },
      [handleFilesSelected, t],
    ),
    onDragEnter: (e) => {
      e.preventDefault();
      dragCount.current++;
      setIsDropable(true);
    },
    onDragLeave: () => {
      dragCount.current--;
      if (dragCount.current === 0) {
        setIsDropable(false);
      }
    },
  });

  const handleRemoveFile = React.useCallback(
    (index: number) => {
      const newFiles = imageFiles.filter((_, i) => i !== index);
      setImageFiles(newFiles);

      if (isDefined(context)) {
        context.setImageFiles(newFiles);
      }
    },
    [imageFiles, context],
  );

  const selectFilesButton = (
    <FileSelectionButton
      accept={supportedImageFileTypes.join(',')}
      color="secondary"
      leftIcon={{ icon: ['fad', 'upload'] }}
      multiple={true}
      text={t('fileManager.uploadFilesDialog.chooseFilesButton', { ns: 'components' })}
      variant="contained"
      onFilesSelected={(files) => {
        if (files.length > 0) {
          void handleFilesSelected(files);
        }
      }}
    />
  );

  const columns = useMemo<DataTableColumns<TableItem>>(
    () => ({
      statusIcon: {
        title: t('geodataSetup.uploadImages.status', { ns: 'cloudProcessing' }),
        alignment: 'center',
        width: '60px',
        unfilterable: true,
        render: (item: TableItem) => item.statusIcon,
      },
      filename: {
        title: t('geodataSetup.uploadImages.filename', { ns: 'cloudProcessing' }),
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
        alignment: 'left',
        render: (item: TableItem) => item.filename,
      },
      actions: {
        title: '',
        alignment: 'center',
        width: '60px',
        unfilterable: true,
        render: (item: TableItem) => item.actions,
      },
    }),
    [t],
  );

  const tableItems = useMemo<TableItem[]>(() => {
    return imageFiles.map((fileData, index) => ({
      originalData: fileData,
      filename: fileData.filename,
      statusIcon: (
        <Icon
          color={theme.color.green}
          icon={['fas', 'check-circle']}
          title={t('geodataSetup.uploadImages.valid', { ns: 'cloudProcessing' })}
        />
      ),
      actions: (
        <Icon
          color={theme.color.gray.medium}
          icon={['fas', 'trash-alt']}
          style={{ cursor: 'pointer' }}
          title={t('geodataSetup.uploadImages.removeFile', { ns: 'cloudProcessing' })}
          onClick={() => handleRemoveFile(index)}
        />
      ),
    }));
  }, [imageFiles, theme, handleRemoveFile, t]);

  // Show dropzone when: no images added OR dragging new files
  const showDropzone = imageFiles.length === 0 || isDropable;
  // Show table when: images are added AND not dragging new files
  const showTableWithImages = imageFiles.length > 0 && !isDropable;

  const titleText = useMemo(() => {
    return `${t('geodataSetup.uploadImages.heading', { ns: 'cloudProcessing' })} (${imageFiles.length})`;
  }, [imageFiles.length, t]);

  return (
    <OverlayLoader height={'100%'} visible={isLoading}>
      <Container>
        <Content {...getRootProps()}>
          {/* Show dropzone when no images or when dragging */}
          {showDropzone && (
            <Stack height="100%">
              <h4>{titleText}</h4>
              <DragAndDropInfo>
                <IconPanel
                  header={{ icon: { icon: ['fad', 'images'], opacity: '50%', size: '5x' } }}
                  responsiveness={{ compressed: false }}
                >
                  {{
                    title: t('geodataSetup.uploadImages.addImagesTitle', {
                      fileTypes: supportedImageFileTypes.join(', '),
                      ns: 'cloudProcessing',
                    }),
                    info: (
                      <Trans
                        components={{ selectFilesButton: selectFilesButton }}
                        i18nKey="geodataSetup.dragAndDropInfo"
                        ns="cloudProcessing"
                      />
                    ),
                  }}
                </IconPanel>
              </DragAndDropInfo>
            </Stack>
          )}

          {/* Show table when images are added and not dragging */}
          {showTableWithImages && (
            <FilesContainer>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <h4>{titleText}</h4>
                {selectFilesButton}
              </Stack>
              <DataTable
                columns={columns}
                fixedLayout={true}
                items={tableItems}
                pageSize={10}
                tableStyle={{
                  rowCell: {
                    fontSize: '14px',
                  },
                  headerCell: {
                    fontSize: '14px',
                  },
                }}
              />
            </FilesContainer>
          )}

          {error && (
            <ErrorInfoBox>
              <InfoBox color="red">
                <ErrorContent>
                  <ErrorMessage>{error}</ErrorMessage>
                  {failedImageNames.length > 0 && (
                    <FailedImagesList>
                      {failedImageNames.slice(0, 50).map((name, index) => (
                        <FailedImageItem key={index}>{name}</FailedImageItem>
                      ))}
                      {failedImageNames.length > 50 && (
                        <FailedImageItem>
                          {t('geodataSetup.controlImages.moreItems', {
                            count: failedImageNames.length - 50,
                            ns: 'cloudProcessing',
                          })}
                        </FailedImageItem>
                      )}
                    </FailedImagesList>
                  )}
                </ErrorContent>
              </InfoBox>
            </ErrorInfoBox>
          )}
        </Content>
      </Container>
    </OverlayLoader>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const DragAndDropInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #000;
  padding: 2em;
  height: 100%;
  width: 100%;
  flex: 1;

  button {
    margin: 0.75em auto 0;
  }
`;

const FilesContainer = styled(Stack)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  width: 100%;

  /* Prevent horizontal scrolling */
  > * {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const ErrorInfoBox = styled.div`
  margin-top: 1rem;
`;

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ErrorMessage = styled.div`
  font-weight: 500;
`;

const FailedImagesList = styled.div`
  background-color: white;
  max-height: 200px;
  overflow-y: auto;
  padding-left: 1rem;
  margin-top: 0.5rem;
  border: 1px solid ${(props) => props.theme.color.gray.light};
`;

const FailedImageItem = styled.div`
  padding: 0.25rem 0;
  font-size: 0.9em;
  word-break: break-word;
`;
